/**
 * Parent home alerts — GET /me/alerts (PWA Home strip).
 */

import type { ClubAnnouncement } from './announcements';
import type { TrialOutcome } from './index';

export interface SessionAlert {
  session_id: string;
  swimmer_id: string;
  swimmer_name: string;
  date: string;
  start_time: string;
  squad: string;
  pool: string;
  when_label: 'today' | 'tomorrow';
  /** Set when a relief coach is covering this session. */
  coach_note?: string | null;
}

export type TrialAlertUrgency = 'normal' | 'near_complete' | 'completed';

export interface TrialAlert {
  swimmer_id: string;
  swimmer_name: string;
  sessions_completed: number;
  sessions_limit: number;
  trial_outcome: TrialOutcome;
  urgency: TrialAlertUrgency;
}

export interface FeeAlert {
  /** Invoice or store-order id. Stable across a pay-then-reverse cycle. */
  id?: string;
  kind: 'membership' | 'squad_monthly' | 'store';
  label: string;
  status: 'due' | 'overdue' | 'partial';
  detail?: string;
  swimmer_name?: string;
  amount_due?: number | null;
  due_date?: string | null;
}

export interface HomeAlertsResponse {
  generated_at: string;
  sessions: SessionAlert[];
  trials: TrialAlert[];
  fees: FeeAlert[];
  announcements: ClubAnnouncement[];
}

/** Parent-facing trial progress copy (no officer workflow). */
/** Home alert heading. Matches the parent-app fee card. */
export function feeAlertHeading(fee: Pick<FeeAlert, 'status' | 'kind' | 'label'>): string {
  const store = fee.kind === 'store';
  const title =
    fee.status === 'overdue'
      ? store
        ? 'Merch unpaid / overdue'
        : 'Fees overdue'
      : fee.status === 'partial'
        ? 'Partially paid'
        : store
          ? 'Store order unpaid'
          : 'Payment due';
  return `${title} — ${fee.label}`;
}

/** Second line on the parent-app fee card. Null when there is no balance. */
export function feeAlertAmountLine(fee: Pick<FeeAlert, 'amount_due' | 'due_date'>): string | null {
  if (fee.amount_due == null) return null;
  const due = `$${Number(fee.amount_due).toFixed(2)} due`;
  return fee.due_date ? `${due} · ${fee.due_date}` : due;
}

export function trialAlertMessage(trial: TrialAlert): string {
  const { sessions_completed, sessions_limit, trial_outcome } = trial;
  if (trial_outcome === 'completed' || sessions_completed >= sessions_limit) {
    return `Free trial complete (${sessions_limit}/${sessions_limit}). The club will be in touch about membership — no action needed from you right now.`;
  }
  if (sessions_completed === 0) {
    return `Free trial — ${sessions_limit} pool sessions included before joining.`;
  }
  return `Free trial progress: ${sessions_completed}/${sessions_limit} sessions attended.`;
}
